/**
 * "New campaign" CTA for A8 Orchestrator. Operator describes an SEO goal
 * in plain English; orchestrator parses + decomposes + queues an
 * approval-gated plan.
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { Sparkles, Target } from 'lucide-react';
import { toast } from 'sonner';
import type { Campaign, Project } from '@seo/types';
import { Button, type ButtonProps } from '@/components/ui/button';
import { FormDialog, FormDialogBlock } from '@/components/ui/form-dialog';
import { TextField, TextAreaField, FormField } from '@/components/ui/form-field';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractFieldErrors, summarizeApiError } from '@/lib/api';
import { useCreateCampaign } from '@/features/campaigns/use-create-campaign';

const CampaignFormSchema = z.object({
  project_id: z.string().uuid({ message: 'Pick a project' }),
  goal: z
    .string()
    .trim()
    .min(10, 'Describe the goal in at least 10 characters')
    .max(1000, 'Too long'),
  horizon_days: z
    .number({ invalid_type_error: 'Enter a number' })
    .int()
    .min(7)
    .max(365)
    .default(90),
  constraints: z.string().trim().max(2000).optional(),
  auto_approve: z.boolean().default(true),
});
type CampaignFormValues = z.infer<typeof CampaignFormSchema>;

export interface CampaignCreateButtonProps {
  projects: Project[];
  defaultProjectId?: string;
  label?: string;
  buttonProps?: Omit<ButtonProps, 'onClick'>;
  navigateOnSuccess?: boolean;
}

export function CampaignCreateButton({
  projects,
  defaultProjectId,
  label = 'New campaign',
  buttonProps,
  navigateOnSuccess = true,
}: CampaignCreateButtonProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const trigger = (
    <Button {...buttonProps}>
      <Sparkles className="h-4 w-4" />
      {label}
    </Button>
  );

  if (projects.length === 0) {
    return (
      <FormDialogBlock
        open={open}
        onOpenChange={setOpen}
        trigger={trigger}
        title="No projects yet"
        description="Create a project first — campaigns are scoped to a project."
      />
    );
  }

  return (
    <CampaignDialog
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      projects={projects}
      defaultProjectId={defaultProjectId}
      navigateOnSuccess={navigateOnSuccess}
    />
  );
}

function CampaignDialog({
  open,
  onOpenChange,
  trigger,
  projects,
  defaultProjectId,
  navigateOnSuccess,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trigger: JSX.Element;
  projects: Project[];
  defaultProjectId?: string;
  navigateOnSuccess: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const create = useCreateCampaign();
  const initialProjectId =
    defaultProjectId ?? (projects.length === 1 ? projects[0]?.id : '') ?? '';

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CampaignFormValues>({
    resolver: zodResolver(CampaignFormSchema),
    defaultValues: {
      project_id: initialProjectId,
      goal: '',
      horizon_days: 90,
      constraints: '',
      auto_approve: true,
    },
    mode: 'onBlur',
  });

  const projectId = watch('project_id');
  const autoApprove = watch('auto_approve');

  useEffect(() => {
    if (!projectId && projects.length === 1) {
      setValue('project_id', projects[0]!.id, { shouldValidate: false });
    }
  }, [projectId, projects, setValue]);

  const submit = handleSubmit(async (values) => {
    try {
      const created = await create.mutateAsync({
        project_id: values.project_id,
        goal: values.goal,
        horizon_days: values.horizon_days,
        ...(values.constraints ? { constraints: values.constraints } : {}),
        auto_approve: values.auto_approve,
      });
      toast.success('Campaign queued', {
        description: `${values.horizon_days}-day horizon`,
      });
      reset();
      onOpenChange(false);
      if (navigateOnSuccess) {
        await navigate({
          to: '/campaigns/$campaignId',
          params: { campaignId: (created as Campaign).id },
        });
      }
    } catch (e) {
      const fieldErrors = extractFieldErrors(e);
      for (const f of ['project_id', 'goal', 'horizon_days', 'constraints'] as const) {
        if (fieldErrors[f]) setError(f, { type: 'server', message: fieldErrors[f] });
      }
      const summary = summarizeApiError(e, {
        fallbackTitle: 'Could not start campaign',
        fieldLabels: {
          project_id: 'Project',
          goal: 'Goal',
          horizon_days: 'Time horizon',
          constraints: 'Constraints',
        },
      });
      toast.error(summary.title, { description: summary.description });
    }
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      icon={<Target className="h-5 w-5 text-primary" />}
      title="Run an autonomous campaign"
      description="Describe the goal in plain English. The Orchestrator will parse it, pick a strategy, and queue a DAG of specialist agents."
      size="lg"
      submitLabel={autoApprove ? 'Run campaign' : 'Plan campaign'}
      submitting={isSubmitting || create.isPending}
      onSubmit={submit}
    >
      <FormField label="Project" error={errors.project_id?.message}>
        <Select
          value={projectId}
          onValueChange={(v) => setValue('project_id', v, { shouldValidate: true })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Pick a project" />
          </SelectTrigger>
          <SelectContent>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      <TextAreaField
        label="Goal"
        rows={4}
        placeholder="Grow organic traffic 25% on the /pricing folder in 90 days"
        hint="Mention the metric, the scope, and the time horizon for best results."
        error={errors.goal?.message}
        {...register('goal')}
      />

      <div className="grid grid-cols-2 gap-3">
        <TextField
          label="Horizon (days)"
          type="number"
          min={7}
          max={365}
          error={errors.horizon_days?.message}
          {...register('horizon_days', { valueAsNumber: true })}
        />
        <div className="flex items-center gap-2 pt-6">
          <input
            id="campaign-auto"
            type="checkbox"
            checked={autoApprove}
            onChange={(e) => setValue('auto_approve', e.target.checked)}
            className="h-4 w-4 rounded border-border/70 accent-primary"
          />
          <Label htmlFor="campaign-auto" className="cursor-pointer">
            Auto-approve plan
          </Label>
        </div>
      </div>

      <TextAreaField
        label="Constraints (optional)"
        rows={2}
        placeholder="e.g. no new technical debt, capacity is 4 briefs/month"
        error={errors.constraints?.message}
        {...register('constraints')}
      />
    </FormDialog>
  );
}
